import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { concatMap, EMPTY, filter, interval, Observable, of, scan, Subject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Address,
  ContractDuration,
  Language,
  PersonWithProducts,
  ProcessId,
  Status,
  StatusResponse,
  Submissions,
  SubmissionsService,
} from '../../core/api';
import { first, map, switchMap } from 'rxjs/operators';
import { changeVisibilityOfBackendError } from '../../core/states/app/app.actions';
import { Store } from '@ngrx/store';
import { ApiLanguageService } from '../../core/services/language/api-language.service';
import { EnvironmentService } from '../../core/services/environment/environment.service';

type SecretArea = 'LEFT_TOP_CORNER' | 'RIGHT_TOP_CORNER';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-dev-tools',
  templateUrl: './dev-tools.component.html',
  styleUrls: ['./dev-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevToolsComponent implements OnInit {
  private static secretPattern: SecretArea[] = [
    'LEFT_TOP_CORNER',
    'RIGHT_TOP_CORNER',
    'RIGHT_TOP_CORNER',
    'LEFT_TOP_CORNER',
  ];
  public isOpen = false;
  private clickedAreaSubject = new Subject<SecretArea>();
  public showLoader$: Observable<boolean>;

  private address: Address = {
    city: 'Biel/Bienne',
    bfs_number: 371,
    house_number: '1',
    street: 'Strässli',
    zip_code: '2500',
  };

  private personsWithProducts: PersonWithProducts[] = [
    {
      products_request: {
        basic_insurance: {
          insurer: 'VISANA',
          franchise: 'ADULT_2500',
          includes_accident_coverage: false,
          model_type: 'TRADITIONAL',
          medic_zsr: undefined,
          general_practitioner_zsr: undefined,
        },
        supplementary_insurances: {
          basic: { type: 'BASIC_GENERAL' },
          legal_protection: true,
        },
      },
      contact_data: { email: 'd@d.com', phone: '+41798887733' },
      date_of_birth: '1990-05-05',
      gender: 'MALE',
      first_name: 'Fredy' + this.getRandomNumber(),
      last_name: 'Krüger',
    },
    {
      products_request: {
        basic_insurance: {
          insurer: 'VISANA',
          franchise: 'CHILD_0',
          includes_accident_coverage: true,
          model_type: 'COMBI_CARE',
          medic_zsr: 'A648502',
          general_practitioner_zsr: 'A648502',
        },
        supplementary_insurances: {
          complementary: { level: 'LEVEL_TWO' },
          outpatient: { level: 'LEVEL_TWO' },
          hospital: { type: 'HOSPITAL_GENERAL' },
        },
      },
      date_of_birth: '2015-03-02',
      gender: 'FEMALE',
      first_name: 'Laura' + this.getRandomNumber(),
      last_name: 'Krüger',
    },
  ];

  private saleOfferBegin = new Date().getFullYear() + 1 + '-01-01';
  private contractDuration: ContractDuration = 'THREE_YEARS';

  private submissions: Submissions = {
    persons: this.personsWithProducts,
    address: this.address,
    language: this.apiLanguageService.getCurrentLanguageForApiOrUndefined() ?? Language.De,
    sale_offer_begin: this.saleOfferBegin,
    contract_duration: this.contractDuration,
    utm_parameters: {
      source: 'dev_source',
      campaign: 'dev_campaign',
      medium: 'dev_medium',
    },
    framework_contract: undefined,
  };

  constructor(
    private store: Store,
    private apiLanguageService: ApiLanguageService,
    public submissionsService: SubmissionsService,
    private entryService: EnvironmentService,
  ) {}

  private static setValueAndMarkDirty(inputElement: HTMLInputElement, value: string): void {
    inputElement.value = value;
    inputElement.dispatchEvent(new Event('input'));
  }

  private static fillPersonData(): void {
    const streetInput = document.querySelector('input[data-cy="input-street"]');
    const houseNumberInput = document.querySelector('input[data-cy="input-house-number"]');

    if (streetInput) {
      DevToolsComponent.setValueAndMarkDirty(streetInput as HTMLInputElement, 'Musterstrasse');
    }
    if (houseNumberInput) {
      DevToolsComponent.setValueAndMarkDirty(houseNumberInput as HTMLInputElement, '22a');
    }
  }

  private static fillMobileNumberAndEmail(): void {
    const mobileNumberInputs = document.querySelectorAll('input[data-cy="input-phone"]');
    const emailInputs = document.querySelectorAll('input[data-cy="input-email"]');

    if (mobileNumberInputs) {
      let counter = 10;
      mobileNumberInputs.forEach((mobileNumber) =>
        DevToolsComponent.setValueAndMarkDirty(mobileNumber as HTMLInputElement, `+41 79 555 22 ${counter++}`),
      );
    }
    if (emailInputs) {
      let counter = 0;
      emailInputs.forEach((email) =>
        DevToolsComponent.setValueAndMarkDirty(email as HTMLInputElement, `devtools${counter++}@ssc.ch`),
      );
    }
  }

  public skipToAssistant(): void {
    this.showLoader$ = of(true);
    this.submitSubmission$()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (url) => {
          this.showLoader$ = of(false);
          window.location.href = url;
        },
        error: () => {
          this.showLoader$ = of(false);
          this.store.dispatch(
            changeVisibilityOfBackendError({
              showBackendErrorPopup: true,
            }),
          );
        },
      });
  }

  public fillContactData(): void {
    DevToolsComponent.fillMobileNumberAndEmail();
    DevToolsComponent.fillPersonData();
    this.isOpen = false;
  }

  @HostListener('document:click', ['$event'])
  public onSecretClick(event: PointerEvent): void {
    const secretAreaInPixel = 50;
    const rightClickAreaWidth = window.innerWidth - secretAreaInPixel;

    if (event.y <= secretAreaInPixel) {
      if (event.x <= secretAreaInPixel) {
        this.clickedAreaSubject.next('LEFT_TOP_CORNER');
      } else if (event.x >= rightClickAreaWidth) {
        this.clickedAreaSubject.next('RIGHT_TOP_CORNER');
      }
    }
  }

  public ngOnInit(): void {
    this.entryService
      .getEnvironment$()
      .pipe(
        untilDestroyed(this),
        switchMap((env) => {
          if (env.environment !== 'PRD01') {
            return this.clickedAreaSubject.pipe(
              scan((acc: string[], val) => {
                acc.push(val);
                return acc.slice(-DevToolsComponent.secretPattern.length);
              }, []),
            );
          } else {
            return EMPTY;
          }
        }),
        untilDestroyed(this),
      )
      .subscribe((pattern) => {
        if (DevToolsComponent.secretPattern.every((area, index) => pattern[index] === area)) {
          this.isOpen = true;
        }
      });
  }

  private submitSubmission$(): Observable<string | 'error'> {
    return this.submissionsService.submit(this.submissions).pipe(
      concatMap((processId) => {
        return this.pollingSubmissionStatus$(processId).pipe(
          concatMap((response) => {
            if (response.status === Status.Ready) {
              return this.submissionsService
                .getEntryUrl(processId.process_id)
                .pipe(map((urlResponse) => urlResponse.url));
            } else {
              return of('error');
            }
          }),
        );
      }),
    );
  }

  private pollingSubmissionStatus$(processId: ProcessId): Observable<StatusResponse> {
    return interval(2000).pipe(
      concatMap(() => this.submissionsService.getStatus(processId.process_id)),
      filter((response) => response.status !== Status.InProgress),
      first(),
    );
  }

  private getRandomNumber(): number {
    return Math.floor(Math.random() * (1000 - 1) + 1);
  }
}
